import React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Info,
  LoaderCircle,
  TriangleAlert,
} from "lucide-react";

export type AppToastType =
  | "success"
  | "error"
  | "warning"
  | "info"
  | "loading"
  | "default";

interface AppToastContentProps {
  type?: AppToastType;
  title?: React.ReactNode;
  message: React.ReactNode;
}

const toastAppearance: Record<
  AppToastType,
  {
    title: string;
    icon: React.ReactNode;
    iconClassName: string;
    accentClassName: string;
  }
> = {
  success: {
    title: "Success",
    icon: <CheckCircle2 size={18} strokeWidth={2} />,
    iconClassName: "border-emerald-100 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-300",
    accentClassName: "bg-emerald-500",
  },
  error: {
    title: "Something went wrong",
    icon: <AlertCircle size={18} strokeWidth={2} />,
    iconClassName: "border-red-100 bg-red-50 text-red-700 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-300",
    accentClassName: "bg-red-500",
  },
  warning: {
    title: "Attention needed",
    icon: <TriangleAlert size={18} strokeWidth={2} />,
    iconClassName: "border-amber-100 bg-amber-50 text-amber-700 dark:border-amber-400/20 dark:bg-amber-400/10 dark:text-amber-300",
    accentClassName: "bg-amber-500",
  },
  info: {
    title: "Update",
    icon: <Info size={18} strokeWidth={2} />,
    iconClassName: "border-purple-100 bg-purple-50 text-[#6D4DB3] dark:border-purple-400/20 dark:bg-purple-400/10 dark:text-purple-300",
    accentClassName: "bg-[#7E57C2]",
  },
  loading: {
    title: "Working",
    icon: <LoaderCircle size={18} className="animate-spin" strokeWidth={2} />,
    iconClassName: "border-purple-100 bg-purple-50 text-[#6D4DB3] dark:border-purple-400/20 dark:bg-purple-400/10 dark:text-purple-300",
    accentClassName: "bg-[#7E57C2]",
  },
  default: {
    title: "Notification",
    icon: <Info size={18} strokeWidth={2} />,
    iconClassName: "border-gray-200 bg-gray-50 text-gray-600 dark:border-white/10 dark:bg-zinc-800 dark:text-zinc-300",
    accentClassName: "bg-gray-400",
  },
};

const AppToastContent: React.FC<AppToastContentProps> = ({
  type = "default",
  title,
  message,
}) => {
  const appearance = toastAppearance[type];

  return (
    <div className="waivera-toast-content waivera-app-toast-content relative flex min-w-0 flex-1 items-start gap-3 overflow-hidden px-0.5 py-0.5">
      <span
        className={`absolute -left-3 top-0 h-full w-[3px] rounded-r-full ${appearance.accentClassName}`}
        aria-hidden="true"
      />

      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${appearance.iconClassName}`}
        aria-hidden="true"
      >
        {appearance.icon}
      </div>

      <div className="min-w-0 flex-1 pt-0.5">
        <p className="m-0 text-[13px] font-semibold leading-snug text-gray-900 dark:text-zinc-100">
          {title ?? appearance.title}
        </p>

        <div className="mt-1 break-words text-[12px] font-normal leading-relaxed text-gray-600 dark:text-zinc-300">
          {message}
        </div>
      </div>
    </div>
  );
};

export default AppToastContent;
